/**
 * rk-cart-gift — Gift product card custom element.
 *
 * – Click "adicionar" expands the card to show a textarea + "confirmar" button.
 * – Click "confirmar" adds the configured gift product to the cart with a
 *   `properties[Recado]` line item property (only if the shopper typed a note).
 * – Dispatches `cart:update` so the cart drawer/page re-renders.
 */
class RkCartGift extends HTMLElement {
  connectedCallback() {
    this.addEventListener('click', this.#handleClick);
  }

  disconnectedCallback() {
    this.removeEventListener('click', this.#handleClick);
  }

  #handleClick = (/** @type {MouseEvent} */ e) => {
    const target = e.target instanceof Element ? e.target : null;
    if (!target) return;

    const addBtn = target.closest('[data-action="add"]');
    if (addBtn) {
      this.#expand();
      return;
    }

    const cancelBtn = target.closest('[data-action="cancel"]');
    if (cancelBtn) {
      this.#collapse();
      return;
    }

    const confirmBtn = /** @type {HTMLButtonElement | null} */ (
      target.closest('[data-action="confirm"]')
    );
    if (confirmBtn) {
      this.#addToCart(confirmBtn);
    }
  };

  #expand() {
    this.classList.add('rk-cart-gift--expanded');

    const expand = /** @type {HTMLElement | null} */ (
      this.querySelector('.rk-cart-gift__expand')
    );
    if (expand) expand.hidden = false;

    const textarea = /** @type {HTMLTextAreaElement | null} */ (
      this.querySelector('[name="gift_note"]')
    );
    if (textarea) {
      /* Wait a tick so the element is visible before focusing */
      requestAnimationFrame(() => textarea.focus());
    }
  }

  #collapse() {
    this.classList.remove('rk-cart-gift--expanded');

    const expand = /** @type {HTMLElement | null} */ (
      this.querySelector('.rk-cart-gift__expand')
    );
    if (expand) expand.hidden = true;

    const textarea = /** @type {HTMLTextAreaElement | null} */ (
      this.querySelector('[name="gift_note"]')
    );
    if (textarea) textarea.value = '';

    const addBtn = /** @type {HTMLElement | null} */ (
      this.querySelector('[data-action="add"]')
    );
    addBtn?.focus();
  }

  /** @param {HTMLButtonElement} btn */
  #addToCart = async (btn) => {
    const variantId = Number(this.dataset['variantId']);
    if (!variantId) return;

    const textarea = /** @type {HTMLTextAreaElement | null} */ (
      this.querySelector('[name="gift_note"]')
    );
    const note = (textarea?.value || '').trim();

    const propertyName = this.dataset['propertyName'] || 'Recado';

    /** @type {Record<string, string>} */
    const properties = {};
    if (note) properties[propertyName] = note;

    btn.disabled = true;
    btn.setAttribute('aria-busy', 'true');

    try {
      const response = await fetch(`${window.Shopify.routes.root}cart/add.js`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Accept: 'application/json',
        },
        body: JSON.stringify({
          items: [
            {
              id: variantId,
              quantity: 1,
              properties,
            },
          ],
        }),
      });

      if (!response.ok) {
        throw new Error(`Add to cart failed: ${response.status}`);
      }

      this.#dispatchCartUpdate();

      /* Immediate feedback: remove the card. The cart re-render will keep it
         hidden because the liquid early-returns when the gift product is
         already in the cart. */
      this.remove();
    } catch (err) {
      console.error('[rk-cart-gift]', err);
      btn.disabled = false;
      btn.removeAttribute('aria-busy');
    }
  };

  #dispatchCartUpdate() {
    document.dispatchEvent(
      new CustomEvent('cart:update', {
        bubbles: true,
        detail: { data: { source: 'rk-cart-gift' } },
      })
    );
  }
}

if (!customElements.get('rk-cart-gift')) {
  customElements.define('rk-cart-gift', RkCartGift);
}
